import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartDirective } from 'highcharts-angular';
import { StylingTokensService } from '../../styling-tokens/styling-tokens.service';
import { CssVariableSnippetComponent } from '../../../../shared/components/css-variable-snippet/css-variable-snippet.component';
import { StylingCardShellComponent } from '../styling-card-shell/styling-card-shell.component';

const DEFAULT_POINT_COUNT = 12;

const DEFAULT_COLOR = '#D9D9D9';

interface DataVisPointCustom {
    readonly cssVariable: string;
}

@Component({
    selector: 'avd-data-vis-color-chart-card',
    standalone: true,
    templateUrl: './data-vis-color-chart-card.component.html',
    styleUrls: ['./data-vis-color-chart-card.component.scss'],
    imports: [
        StylingCardShellComponent,
        HighchartsChartDirective,
        CssVariableSnippetComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataVisColorChartCardComponent implements OnChanges {
    private readonly stylingTokensService = inject(StylingTokensService);

    @Input() label: string = '';
    @Input() tint: string = 'opaque';
    @Input() inverseMode: boolean = false;

    chartOptions: Highcharts.Options = this.buildChartOptions([]);
    cssVariableSnippet: string = '';

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tint'] || changes['inverseMode']) {
            this.updateChartOptions();
        }
    }

    private updateChartOptions(): void {
        const previewItems = this.stylingTokensService.getDataVisColorItems(
            this.tint,
            DEFAULT_POINT_COUNT,
            this.inverseMode,
        );

        const data: Highcharts.PointOptionsObject[] = previewItems.map(
            (item) => ({
                name: item.label,
                y: 1,
                color: `var(${item.cssVariable}, ${item.colorValue ?? DEFAULT_COLOR})`,
                custom: {
                    cssVariable: item.cssVariable,
                } as DataVisPointCustom,
            }),
        );

        this.cssVariableSnippet =
            this.stylingTokensService.buildDataVisCssVariableSnippet(
                previewItems,
            );

        this.chartOptions = this.buildChartOptions(data);
    }

    private buildChartOptions(
        data: Highcharts.PointOptionsObject[],
    ): Highcharts.Options {
        const series: Highcharts.SeriesPieOptions = {
            type: 'pie',
            name: 'Registrations',
            innerSize: '50%',
            data,
        };

        return {
            chart: {
                type: 'pie',
                spacing: [8, 8, 8, 8],
            },
            title: {
                text: `Data Visualisation colors - ${this.tint}`,
                style: {
                    color: 'var(--uilab-sys-color-text-neutral-medium-emphasis, #1f1f1f)',
                },
            },
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    const custom = this.options.custom as
                        | DataVisPointCustom
                        | undefined;
                    return custom?.cssVariable ?? '';
                },
            },
            legend: {
                enabled: true,
            },
            credits: {
                enabled: false,
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    borderRadius: 8,
                    dataLabels: {
                        enabled: false,
                        distance: 20,
                        format: '{point.name}',
                    },
                    showInLegend: true,
                },
            },
            series: [series],
        };
    }
}
